import { Component, ViewEncapsulation } from '@angular/core';
import { CampaignSettingsAbstractComponent } from '../campaign-settings-abstract.component';
import {
  Campaign,
  CampaignSection,
  LifelinePhoneSettingOptions,
  Pager,
  Phone,
  PhoneNumberTypeLabels,
  PhoneProviderTypeLabels,
  PhoneStatus,
  PhoneStatusLabels,
  PhoneUseTypeEnum,
  User,
} from '../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  LoaderService,
  PhoneService,
  SideNavService,
  UserService,
} from '../../_services';
import { FormBuilder, Validators } from '@angular/forms';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { takeUntil } from 'rxjs/operators';
import { expandCollapse } from '../../_shared/animations/app-animations';

@Component({
  templateUrl: './campaign-phone-setup.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-phone-setup.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [expandCollapse],
})
export class CampaignPhoneSetupComponent extends CampaignSettingsAbstractComponent {
  public section = CampaignSection.PhoneSetup;
  timezones: string[] = [];
  public phones: Phone[] = [];
  public selectedPhone: Phone = null;
  public showPhonePopup: boolean = false;
  public isAdvancedSettingsEnabled: boolean = false;
  public user: User = null;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected modalService: NgxSmartModalService,
    protected loaderService: LoaderService,
    protected sideNav: SideNavService,
    protected phoneService: PhoneService,
    protected userService: UserService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      phone_input_timeout: [5, [Validators.required, Validators.min(1)]],
      call_recording_enabled: [false],
      call_frequency_timeout: [5, [Validators.required, Validators.min(0)]],
      call_frequency_threshold: [3, [Validators.required, Validators.min(0)]],
      call_frequency_afterhours: [false],
      sms_domain: [null],
      alpha_sender_id: [null, [Validators.maxLength(11)]],
    });
    super.ngOnInit();

    this.userService.getCurrent().subscribe(
      (user: User) => {
        this.user = user;
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  onCampaignFetch(campaign: Campaign) {
    this.loaderService.show();
    this.phoneService.list({}).subscribe(
      (data: Pager) => {
        this.phones = data.results.filter((phone: Phone) => phone.campaign === null || phone.campaign === campaign.id);
        this.loaderService.hide();
      },
      (error) => {
        this.handleError(error);
        this.loaderService.hide();
      }
    );

    this.form.patchValue(campaign);
  }

  getType(phone: Phone) {
    return PhoneNumberTypeLabels[phone.type];
  }

  getStatus(phone: Phone) {
    return PhoneStatusLabels[phone.status];
  }

  getUseType(phone: Phone) {
    let useType = 'Voice & SMS';

    switch (phone.use_type) {
      case PhoneUseTypeEnum.SMSOnly:
        useType = 'SMS Only';
        break;
      case PhoneUseTypeEnum.VoiceOnly:
        useType = 'Voice Only';
        break;
      default:
        break;
    }

    return useType;
  }

  getDomain(phone: Phone) {
    if (phone.domain) {
      let customDomain = this.campaign.custom_domains.filter((item) => item.id === phone.domain);

      if (customDomain.length > 0) {
        return customDomain[0].domain;
      }
    }
    return 'Unassigned';
  }

  getProvider(phone: Phone) {
    return PhoneProviderTypeLabels[phone.resourcetype];
  }

  getStatusClass(phone: Phone) {
    let className = 'warning';

    switch (phone.status) {
      case PhoneStatus.Active:
        className = 'success';
        break;
      case PhoneStatus.Deleted:
      case PhoneStatus.Failed:
        className = 'danger';
        break;
      default:
        break;
    }

    return className;
  }

  deletePhone(phone: Phone) {
    const message =
      'The phone number is assigned to ' + this.campaign.display_name + '\nAre you sure you want to delete it?';

    if (window.confirm(message)) {
      this.loaderService.show();

      this.phoneService
        .delete(phone.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (result) => {
            this.alertService.success('Deleted phone number.');
            this.phones = this.phones.filter((item) => item.id !== phone.id);
            this.loaderService.hide();
          },
          (error) => {
            this.handleSubmitError(error);
            this.loaderService.hide();
          }
        );
    }
  }

  onClosePhonePopup() {
    this.selectedPhone = null;
    this.modalService.getModal('phonePopup').close();
    this.showPhonePopup = false;
  }

  openPhonePopup(phone: Phone = null) {
    this.selectedPhone = phone;
    this.modalService.getModal('phonePopup').open();
    this.showPhonePopup = true;
  }

  get isBypassed() {
    return (
      this.isFieldEqual('call_frequency_threshold', '1') && this.isBooleanFieldChecked('call_frequency_afterhours')
    );
  }

  forceBypass(event) {
    if (this.isBypassed) {
      this.form.patchValue({ call_frequency_threshold: 2 });
    } else {
      this.form.patchValue({ call_frequency_threshold: 1, call_frequency_afterhours: true });
    }
  }

  toggleAssignment(phone: Phone, event) {
    const checked = event.target.checked;
    let data: any = {
      resourcetype: phone.resourcetype,
      campaign: this.campaign.id,
    };

    if (!checked) {
      data = { ...data, domain: null, service: null, campaign: null };
    }

    let message = 'Are you sure you want to remove ' + phone.phone + ' from this brand';

    if (phone.domain && this.campaign.custom_domains.length > 0) {
      const domainIndex = this.campaign.custom_domains.findIndex((domain) => domain.id === phone.domain);

      if (domainIndex > -1) {
        message += ' and the ' + this.campaign.custom_domains[domainIndex].domain + ' domain';
      }
    }

    if (checked || window.confirm(message)) {
      this.loaderService.show();
      this.phoneService.patch(phone.id, data).subscribe(
        (phone: Phone) => {
          const index = this.phones.findIndex((item) => item.id === phone.id);
          this.loaderService.hide();

          if (index > -1) {
            this.phones[index] = phone;
          }
        },
        (error) => {
          this.loaderService.hide();
          this.handleError(error);
        }
      );
    }
  }
}
